import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Button/Button'
import { Link } from 'src/components/Link/Link'
import { Price } from 'src/components/Price/Price'
import { Text } from 'src/components/Text/Text'
import { config } from 'src/config'
import { getCalculatedPrices } from 'src/functions/price/getCalculatedPrices'
import { getMetafield } from 'src/functions/util/getMetafield'

import styles from './BuyBox.module.scss'

import type { ProductVariant } from 'src/data/graphql-generated'

interface ShippingInfoProps {
  variant: ProductVariant
  quantity: number
  setShowShippingTermsModal: (set: boolean) => void
  isRenderedWithinModal?: boolean
  isAddingToSubscription?: boolean
}

export const ShippingInfo = ({
  variant,
  quantity,
  setShowShippingTermsModal,
  isRenderedWithinModal = false,
  isAddingToSubscription = false,
}: ShippingInfoProps) => {
  const { t } = useTranslation()
  const { price, unitPrice, subscriptionUnitPrice, subscriptionPrice } = getCalculatedPrices({ variant })

  const relevantPrice = isAddingToSubscription ? subscriptionPrice : price
  const isFreeShipping = relevantPrice.amount * quantity >= config.shippingFreeFrom

  const deliveryTimeOverride = getMetafield(variant.metafields, 'product', 'delivery_time')
  const [min, max] = (deliveryTimeOverride || config.deliveryTime).split('-')

  const vatIncludedText = ` ${t('pdp.vatIncluded')} `

  const shippingCostText = t(`pdp.${isFreeShipping ? 'freeShipping' : 'excludingShipping'}`)
  const shippingTimeText = t('pdp.deliveryTimeframe', { min, max })

  return (
    <Text variant="p" small className={clsx(styles.notice, styles.secondaryText, 'notranslate')} translate="no">
      {unitPrice && (
        <Price
          price={isAddingToSubscription && subscriptionUnitPrice ? subscriptionUnitPrice : unitPrice}
          unitPriceMeasurement={variant.unitPriceMeasurement}
        />
      )}
      {vatIncludedText}
      {isRenderedWithinModal ? (
        <Link
          href={t('footer.green.helpAndFaq.shipping.link')}
          target="_blank"
          aria-label={`${t('common.shipping')} - ${t('common.newTab')}`}
        >
          {shippingCostText}.
        </Link>
      ) : (
        <Button
          onClick={() => setShowShippingTermsModal(true)}
          aria-label={t('common.shipping')}
          inline
          variant="ghost"
          className={styles.shippingButton}
        >
          {shippingCostText}.
        </Button>
      )}{' '}
      {shippingTimeText}. {!isFreeShipping && t('pdp.freeShippingFrom')}
    </Text>
  )
}
